import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { ChevronDown, ChevronUp } from "react-ikonate";
import ColorPickerModal from "shared/ColorPickerModal";
import styles from "./ColorDropdown.module.css";
import CheckMark from "./CheckMark.svg";

export function ColorDropdown({
  options,
  onChange,
  selectedOption,
  isOpen,
  onToggle,
}) {
  const dropdownRef = useRef(null);
  const [isColorPickerModalOpen, setIsColorPickerModalOpen] = useState(false);
  const [hoveredColorLabel, setHoveredColorLabel] = useState("");

  const handleClickOutside = useCallback(
    (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onToggle(false);
      }
    },
    [onToggle],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }

    return undefined;
  }, [isOpen, handleClickOutside]);

  const toggleDropdown = () => onToggle(!isOpen);

  const handleColorChange = (option) => {
    if (option.value === "custom") {
      setIsColorPickerModalOpen(true);
    } else {
      onChange(option.value);
      onToggle(false);
    }
  };

  const handleOnsaveColor = (customColor) => {
    onChange(customColor);
    setIsColorPickerModalOpen(false);
    onToggle(false);
  };

  const selectedOptionObj = options.find(
    (option) =>
      option.value.toUpperCase() === (selectedOption || "").toUpperCase(),
  );
  const selectedLabel = selectedOptionObj ? selectedOptionObj.label : "Choose";

  return (
    <div className={styles.container} ref={dropdownRef}>
      <div className={styles.colorInputContainer}>
        <div
          className={styles.colorPreviewBox}
          style={{ backgroundColor: selectedOption || "#ffffff" }}
        >
          {selectedOption ? (
            <img
              src={CheckMark}
              alt="Checkmark icon"
              className={styles.checkmarkIcon}
            />
          ) : null}
        </div>
        <button
          type="button"
          className={styles.colorInputField}
          onClick={toggleDropdown}
        >
          <span>{selectedLabel}</span>
          <span className={styles.chevronContainer}>
            <ChevronDown className={styles.chevron} />
          </span>
        </button>
      </div>

      {isOpen ? (
        <div className={styles.dropdown}>
          <div
            className={styles.selectedOpenContainer}
            onClick={toggleDropdown}
          >
            <span className={styles.hoveredColorLabel}>
              {hoveredColorLabel || selectedLabel}
            </span>

            <span className={styles.chevronContainer}>
              <ChevronUp className={styles.chevronUp} />
            </span>
          </div>

          <div className={styles.colorOptions}>
            {options.map((option) => (
              <div
                key={option.value}
                className={`${styles.colorOption} ${
                  selectedOptionObj?.value === option.value
                    ? styles.selected
                    : ""
                }`}
                style={{ backgroundColor: option.value }}
                onMouseEnter={() => setHoveredColorLabel(option.label)}
                onMouseLeave={() => setHoveredColorLabel("")}
                onClick={() => handleColorChange(option)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    handleColorChange(option);
                  }
                }}
              >
                {selectedOptionObj?.value === option.value ? (
                  <img
                    src={CheckMark}
                    alt="Selected"
                    className={styles.checkmarkIcon}
                  />
                ) : null}
              </div>
            ))}
          </div>

          <div className={styles.divider} />

          <button
            type="button"
            className={styles.customOption}
            onClick={() => handleColorChange({ value: "custom" })}
          >
            Custom
          </button>
        </div>
      ) : null}

      <ColorPickerModal
        isOpen={isColorPickerModalOpen}
        onCancel={() => {
          setIsColorPickerModalOpen(false);
        }}
        onSave={handleOnsaveColor}
      />
    </div>
  );
}

ColorDropdown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  selectedOption: PropTypes.string,
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func,
};

ColorDropdown.defaultProps = {
  selectedOption: "",
  isOpen: false,
  onToggle: () => {},
};

export default ColorDropdown;
